import type { CommandFactory } from './types'
import { getStoredUser } from '@/lib/api/client'
import { getChainName, isValidPrivateKey, getUSDCBalance } from '@/lib/crypto/wallet'

// This will be injected by the component that uses these commands
// We need access to the wallet context methods
interface WalletCommandContext {
  generateWallet: (userName: string, userId: string) => Promise<{ address: string; privateKey: string }>
  restoreWallet: (privateKey: string, userName: string, userId: string) => Promise<{ address: string }>
  loadWallet: (userId: string) => Promise<void>
  deleteWallet: () => Promise<void>
  hasWallet: boolean
  wallet: { address: string; privateKey: string } | null
}

let walletContext: WalletCommandContext | null = null

export function setWalletContext(context: WalletCommandContext) {
  walletContext = context
}

export const generateWalletCommand: CommandFactory = (context) => ({
  id: 'generate-wallet',
  name: 'generate-wallet',
  description: 'Generate a new wallet secured with passkey',
  acceptsArgs: false,
  handler: async () => {
    const user = await getStoredUser()
    if (!user?.id) {
      context.onDisplayMessage?.({
        role: 'command',
        content: '❌ You must be logged in to generate a wallet.',
        timestamp: new Date(),
      })
      return
    }

    if (!walletContext) {
      context.onDisplayMessage?.({
        role: 'command',
        content: '❌ Wallet system not initialized. Please refresh the page.',
        timestamp: new Date(),
      })
      return
    }

    if (walletContext.hasWallet) {
      context.onDisplayMessage?.({
        role: 'command',
        content: '❌ You already have a wallet. Use `/restore` to import a different private key.',
        timestamp: new Date(),
      })
      return
    }

    try {
      context.setIsSearching?.(true)
      context.onDisplayMessage?.({
        role: 'command',
        content: '🔐 Generating new wallet... Please authenticate with your passkey when prompted.',
        timestamp: new Date(),
      })

      const userName = user.email || `User ${user.id}`
      const wallet = await walletContext.generateWallet(userName, user.id)
      const chainName = getChainName()

      context.onDisplayMessage?.({
        role: 'command',
        content: `✅ **Wallet Created Successfully!**

**Network:** ${chainName}
**Address:** \`${wallet.address}\`

---

**SAVE YOUR PRIVATE KEY NOW!**

This is the **ONLY** way to recover your wallet if you lose access to your passkey.

**Your Private Key:**
\`\`\`
${wallet.privateKey}
\`\`\`

- Never share your private key with anyone
- Store it in a secure password manager or write it down and store it in a safe physical location
- Anyone with this key can control your funds
- Use \`/export\` later if you need to view it again

---
This message will disappear when you refresh the page.
`,
        timestamp: new Date(),
      })
    } catch (error) {
      context.onDisplayMessage?.({
        role: 'command',
        content: `❌ Failed to generate wallet: ${error instanceof Error ? error.message : 'Unknown error'}`,
        timestamp: new Date(),
      })
    } finally {
      context.setIsSearching?.(false)
    }
  },
})

export const restoreWalletCommand: CommandFactory = (context) => ({
  id: 'restore-wallet',
  name: 'restore-wallet',
  description: 'Restore wallet from private key',
  acceptsArgs: true,
  handler: async (args?: string) => {
    const user = await getStoredUser()
    if (!user?.id) {
      context.onDisplayMessage?.({
        role: 'command',
        content: '❌ You must be logged in to restore a wallet.',
        timestamp: new Date(),
      })
      return
    }

    if (!walletContext) {
      context.onDisplayMessage?.({
        role: 'command',
        content: '❌ Wallet system not initialized. Please refresh the page.',
        timestamp: new Date(),
      })
      return
    }

    if (!args || args.trim() === '') {
      context.onDisplayMessage?.({
        role: 'command',
        content: '⚠️ Please provide your private key. Usage: `/restore <your-private-key>`\n\nYour private key should start with `0x` followed by 64 hexadecimal characters.',
        timestamp: new Date(),
      })
      return
    }

    const privateKey = args.trim()

    // Validate private key format
    if (!isValidPrivateKey(privateKey)) {
      context.onDisplayMessage?.({
        role: 'command',
        content: '❌ Invalid private key format. It should be 64 hexadecimal characters (optionally prefixed with `0x`).',
        timestamp: new Date(),
      })
      return
    }

    try {
      context.setIsSearching?.(true)
      context.onDisplayMessage?.({
        role: 'command',
        content: '🔐 Restoring wallet... Please authenticate with your passkey when prompted.',
        timestamp: new Date(),
      })

      const userName = user.email || `User ${user.id}`
      const wallet = await walletContext.restoreWallet(privateKey, userName, user.id)
      const chainName = getChainName()

      context.onDisplayMessage?.({
        role: 'command',
        content: `✅ **Wallet Restored Successfully!**

**Network:** ${chainName}
**Address:** \`${wallet.address}\`

Your wallet has been restored and is now secured with your passkey. The private key has been encrypted and stored securely.

Your wallet can now only be accessed with your passkey. Make sure to keep a backup of your private key in a secure location.

💡 **Next steps:**
- Use \`/fund\` to see your wallet address for funding
- Use \`/export\` to re-export your private key if needed
`,
        timestamp: new Date(),
      })
    } catch (error) {
      context.onDisplayMessage?.({
        role: 'command',
        content: `❌ Failed to restore wallet: ${error instanceof Error ? error.message : 'Unknown error'}`,
        timestamp: new Date(),
      })
    } finally {
      context.setIsSearching?.(false)
    }
  },
})

export const viewWalletCommand: CommandFactory = (context) => ({
  id: 'wallet',
  name: 'wallet',
  description: 'Show wallet address for funding',
  acceptsArgs: false,
  handler: async () => {
    const user = await getStoredUser()
    if (!user?.id) {
      context.onDisplayMessage?.({
        role: 'command',
        content: '❌ You must be logged in to view your wallet.',
        timestamp: new Date(),
      })
      return
    }

    if (!walletContext) {
      context.onDisplayMessage?.({
        role: 'command',
        content: '❌ Wallet system not initialized. Please refresh the page.',
        timestamp: new Date(),
      })
      return
    }

    if (!walletContext.hasWallet) {
      context.onDisplayMessage?.({
        role: 'command',
        content: '❌ You don\'t have a wallet yet. Use `/generate` to create a new wallet or `/restore` to import an existing one.',
        timestamp: new Date(),
      })
      return
    }

    try {
      context.setIsSearching?.(true)

      // If wallet is not loaded, load it (requires passkey authentication)
      if (!walletContext.wallet) {
        context.onDisplayMessage?.({
          role: 'command',
          content: '🔐 Loading your wallet... Please authenticate with your passkey when prompted.',
          timestamp: new Date(),
        })
        await walletContext.loadWallet(user.id)
      }

      // After loading, wallet should be available
      if (!walletContext.wallet) {
        context.onDisplayMessage?.({
          role: 'command',
          content: '❌ Failed to load wallet. Please try again.',
          timestamp: new Date(),
        })
        return
      }

      const chainName = getChainName()
      const address = walletContext.wallet.address

      // Fetch USDC balance
      const usdcBalance = await getUSDCBalance(address)

      context.onDisplayMessage?.({
        role: 'command',
        content: `💰 **Your Wallet Address**

**Network:** ${chainName}
**Address:** \`${address}\`
**USDC Balance:** $${usdcBalance}

Send USDC on ${chainName} to this address to fund your wallet.

⚠️ Only send USDC on ${chainName} to this address.
`,
        timestamp: new Date(),
      })
    } catch (error) {
      context.onDisplayMessage?.({
        role: 'command',
        content: `❌ Failed to load wallet: ${error instanceof Error ? error.message : 'Unknown error'}`,
        timestamp: new Date(),
      })
    } finally {
      context.setIsSearching?.(false)
    }
  },
})

export const exportWalletCommand: CommandFactory = (context) => ({
  id: 'export-wallet',
  name: 'export-wallet',
  description: 'Export your private key (requires passkey)',
  acceptsArgs: false,
  handler: async () => {
    const user = await getStoredUser()
    if (!user?.id) {
      context.onDisplayMessage?.({
        role: 'command',
        content: '❌ You must be logged in to export your wallet.',
        timestamp: new Date(),
      })
      return
    }

    if (!walletContext) {
      context.onDisplayMessage?.({
        role: 'command',
        content: '❌ Wallet system not initialized. Please refresh the page.',
        timestamp: new Date(),
      })
      return
    }

    if (!walletContext.hasWallet) {
      context.onDisplayMessage?.({
        role: 'command',
        content: '❌ You don\'t have a wallet yet. Use `/generate` to create a new wallet or `/restore` to import an existing one.',
        timestamp: new Date(),
      })
      return
    }

    try {
      context.setIsSearching?.(true)

      // If wallet is not loaded, load it (requires passkey authentication)
      if (!walletContext.wallet) {
        context.onDisplayMessage?.({
          role: 'command',
          content: '🔐 Loading your wallet... Please authenticate with your passkey when prompted.',
          timestamp: new Date(),
        })
        await walletContext.loadWallet(user.id)
      }

      // After loading, wallet should be available
      if (!walletContext.wallet) {
        context.onDisplayMessage?.({
          role: 'command',
          content: '❌ Failed to load wallet. Please try again.',
          timestamp: new Date(),
        })
        return
      }

      context.onDisplayMessage?.({
        role: 'command',
        content: `🔑 **Wallet Private Key Export**

⚠️ **Handle with care!**

**Your Private Key:**
\`\`\`
${walletContext.wallet.privateKey}
\`\`\`

Anyone with this key can access all your funds.

This is your only backup if you lose access to your passkey. Guard it carefully!
`,
        timestamp: new Date(),
      })
    } catch (error) {
      context.onDisplayMessage?.({
        role: 'command',
        content: `❌ Failed to export wallet: ${error instanceof Error ? error.message : 'Unknown error'}`,
        timestamp: new Date(),
      })
    } finally {
      context.setIsSearching?.(false)
    }
  },
})

export const deleteWalletCommand: CommandFactory = (context) => ({
  id: 'delete-wallet',
  name: 'delete-wallet',
  description: 'Delete your wallet and export private key',
  acceptsArgs: false,
  handler: async () => {
    const user = await getStoredUser()
    if (!user?.id) {
      context.onDisplayMessage?.({
        role: 'command',
        content: '❌ You must be logged in to delete your wallet.',
        timestamp: new Date(),
      })
      return
    }

    if (!walletContext) {
      context.onDisplayMessage?.({
        role: 'command',
        content: '❌ Wallet system not initialized. Please refresh the page.',
        timestamp: new Date(),
      })
      return
    }

    if (!walletContext.hasWallet) {
      context.onDisplayMessage?.({
        role: 'command',
        content: '❌ You don\'t have a wallet to delete.',
        timestamp: new Date(),
      })
      return
    }

    try {
      context.setIsSearching?.(true)

      // SAFETY CHECK: Wallet must be loaded in memory to show private key before deletion
      if (!walletContext.wallet) {
        context.onDisplayMessage?.({
          role: 'command',
          content: '🔐 Loading your wallet... Please authenticate with your passkey when prompted.',
          timestamp: new Date(),
        })
        await walletContext.loadWallet(user.id)
      }

      // After loading, wallet should be available
      if (!walletContext.wallet) {
        context.onDisplayMessage?.({
          role: 'command',
          content: '❌ Failed to load wallet. Cannot delete without displaying your private key first. Please try again.',
          timestamp: new Date(),
        })
        return
      }

      // Wallet is loaded - save the private key before deleting
      const privateKeyBackup = walletContext.wallet.privateKey

      // Delete the wallet
      await walletContext.deleteWallet()

      // Show success message with private key
      context.onDisplayMessage?.({
        role: 'command',
        content: `⚠️ **Wallet Deleted Successfully**

Your wallet has been deleted. If this was a mistake, you can restore it with \`/restore <private-key>\`.

**SAVE THIS PRIVATE KEY NOW** if you want to access it in the future or restore your wallet:

\`\`\`
${privateKeyBackup}
\`\`\`

- Use \`/restore <private-key>\` to restore this wallet
- This is the **ONLY** copy of your private key
- Anyone with this key can access your funds
- This message will disappear when you refresh the page

---

Your wallet has been removed. You can create a new wallet with \`/generate\`.
`,
        timestamp: new Date(),
      })
    } catch (error) {
      context.onDisplayMessage?.({
        role: 'command',
        content: `❌ Failed to delete wallet: ${error instanceof Error ? error.message : 'Unknown error'}`,
        timestamp: new Date(),
      })
    } finally {
      context.setIsSearching?.(false)
    }
  },
})
